import type { PracticeConfiguration } from './rules';
import { bmi, weightGainRange } from './calculations';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePatient, type PatientOut } from '@/features/emr/api/patients-api';
import type { PregnancyOut } from '@/features/emr/types';
import { useMaternalLabs, type LabResult } from '@/features/labs/api';
import { usePermissions } from '@/features/permissions';
import { formatInstant } from '@/lib/utils/date';
import { displayPayload } from './projection';
import { displayWeight, groupPrenatalLabs, pregnancyHeaderModel } from './prenatalHeaderModel';
import type { ClinicalEvent, ClinicalSubject } from './types';
interface HeaderProps {
  configuration?: PracticeConfiguration;
  pregnancy: PregnancyOut;
  events: ClinicalEvent[];
  subjects: ClinicalSubject[];
  timeZone: string;
}
export function PrenatalHeader(props: HeaderProps) {
  const { can } = usePermissions();
  const allowed = can('read_patient');
  const patient = usePatient(allowed ? props.pregnancy.patient_id : undefined);
  const labs = useMaternalLabs(props.pregnancy.id, allowed);
  return (
    <PrenatalHeaderView
      {...props}
      patient={patient.data}
      labs={labs.data?.results}
      labsPregnancyId={props.pregnancy.id}
      labsState={!allowed ? 'restricted' : labs.isError ? 'error' : labs.isLoading ? 'loading' : 'ready'}
      patientUnavailable={!allowed || patient.isError}
    />
  );
}
export function PrenatalHeaderView({
  pregnancy,
  configuration,
  patient,
  events,
  subjects,
  timeZone,
  labs,
  labsPregnancyId,
  labsState = 'ready',
  patientUnavailable = false,
  now = new Date(),
}: HeaderProps & {
  patient?: PatientOut;
  labs?: LabResult[];
  labsPregnancyId?: string;
  labsState?: 'ready' | 'loading' | 'error' | 'restricted';
  patientUnavailable?: boolean;
  now?: Date;
}) {
  const [open, setOpen] = useState(true);
  const data = pregnancyHeaderModel(pregnancy, patient, events, subjects, timeZone, now);
  const refs =
    configuration?.clinically_reviewed && configuration.clinical_references?.enabled
      ? configuration.clinical_references
      : null;
  const calculatedBmi =
    refs && pregnancy.height_inches && data.baselineGrams
      ? bmi(data.baselineGrams / 1000, pregnancy.height_inches * 2.54, refs.bmi_limits)
      : null;
  const gainRange =
    refs && calculatedBmi !== null
      ? weightGainRange(
          calculatedBmi,
          pregnancy.fetus_count === 1
            ? refs.singleton_gain_bands
            : pregnancy.fetus_count === 2
              ? refs.twin_gain_bands
              : [],
        )
      : null;
  const labGroups = groupPrenatalLabs(labsPregnancyId === pregnancy.id ? (labs ?? []) : []);
  const field = (label: string, value: React.ReactNode) => (
    <div key={label} className="min-w-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="break-words">{value || 'Not documented'}</dd>
    </div>
  );
  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Prenatal overview · {data.name ?? 'Name unavailable'}</CardTitle>
            <CollapsibleTrigger asChild>
              <Button variant="outline" className="min-h-11">
                {open ? 'Hide overview' : 'Show overview'}
              </Button>
            </CollapsibleTrigger>
          </div>
          <CardDescription>
            {pregnancy.edd
              ? `EDD ${pregnancy.edd} · ${pregnancy.status === 'active' ? `GA today ${data.ga}` : 'GA today unavailable for closed pregnancy'}`
              : 'EDD not set · gestational age unavailable'}{' '}
            · Pregnancy {pregnancy.id}
          </CardDescription>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="flex flex-col gap-6">
            {patientUnavailable && (
              <Alert>
                <AlertDescription>
                  Patient details could not be loaded. Missing information below is not a negative finding.
                </AlertDescription>
              </Alert>
            )}
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {field('Medical record number', data.mrn)}
              {field(
                'Date of birth / age',
                data.dob ? `${data.dob}${data.age !== null ? ` · ${data.age} years` : ''}` : null,
              )}
              {field(
                'Recorded obstetric history',
                pregnancy.gtpal_display ||
                  `G${pregnancy.gravida} T${pregnancy.term} P${pregnancy.preterm} A${pregnancy.abortions} L${pregnancy.living}`,
              )}
              {field('Dating method', pregnancy.edd_method)}
              {field('LMP', pregnancy.lmp)}
              {field(
                'LMP certainty (documented)',
                data.context.lmp_certain === true ? 'Certain' : data.context.lmp_certain === false ? 'Uncertain' : null,
              )}
              {field('Pregnancy status', pregnancy.status)}
              {field('Recorded allergies', data.allergies.length ? data.allergies.join('; ') : null)}
              {field('Recorded pregnancy problems', pregnancy.problems)}
              {field(
                'VBAC flag in pregnancy record',
                pregnancy.vbac ? 'Marked' : 'Not marked — prior cesarean history not established here',
              )}
              {field('Pre-pregnancy weight', data.baselineGrams !== null ? displayWeight(data.baselineGrams) : null)}
              {field(
                'Latest confirmed maternal weight',
                data.currentWeight ? (
                  <>
                    {displayWeight(data.currentWeight.grams)}
                    <small className="block text-muted-foreground">
                      Observed {formatInstant(data.currentWeight.date, 'MMM d, yyyy HH:mm', timeZone)}
                    </small>
                  </>
                ) : null,
              )}
              {field(
                'Weight change from recorded baseline',
                data.gainGrams !== null
                  ? displayWeight(data.gainGrams)
                  : 'Not calculated — baseline and confirmed weight required',
              )}
              {field(
                'Pre-pregnancy BMI / target gain',
                calculatedBmi === null
                  ? 'Not calculated — valid baseline/height and reviewed reference configuration required'
                  : `BMI ${calculatedBmi.toFixed(1)}${gainRange ? ` · Total gain reference ${gainRange[0]}–${gainRange[1]} lb` : ' · Gain reference unavailable for this BMI/fetus count'}`,
              )}
              {field(
                'Recorded fetal sex',
                pregnancy.fetus_list?.length
                  ? pregnancy.fetus_list
                      .map((f, i) => `${f.label || `Fetus ${i + 1}`}: ${f.sex?.trim() || 'Not documented'}`)
                      .join('; ')
                  : null,
              )}
              {field('Manually documented Rh status', String(data.context.rh_status ?? ''))}
              {field('Manually documented GBS status', String(data.context.gbs_status ?? ''))}
              {field(
                'RhIG documented in clinical context',
                data.context.rhig_documented === true ? 'Yes' : data.context.rhig_documented === false ? 'No' : null,
              )}
              {field('Clinical context source', String(data.context.clinical_source ?? ''))}
              {field('Preferred name', String(data.context.preferred_name ?? ''))}
              {field('Partner / support person', String(data.context.support_person ?? ''))}
              {field('Current medications / supplements (documented)', String(data.context.current_medications ?? ''))}
              {field('Clinical risk flags', String(data.context.risk_flags ?? ''))}
              {field('Assigned midwife', String(data.context.assigned_midwife ?? ''))}
              {field('Next planned visit (documented)', String(data.context.next_visit_date ?? ''))}
              {field(
                'Planned birth site / backup hospital / pediatric provider',
                [
                  data.context.planned_birth_site,
                  data.context.backup_hospital,
                  data.context.backup_phone,
                  data.context.pediatric_provider,
                ]
                  .filter(Boolean)
                  .join(' · '),
              )}
            </dl>
            <section aria-label="Reviewed pregnancy laboratory results" className="flex flex-col gap-3">
              <h3 className="font-medium">Reviewed pregnancy laboratory results</h3>
              <p className="text-sm text-muted-foreground">
                Results and status are copied as documented for this pregnancy. Unlisted or absent results are not
                interpreted as negative.
              </p>
              {labsState === 'loading' && <p>Loading reviewed results…</p>}
              {labsState === 'error' && <p role="alert">Reviewed laboratory results could not be loaded.</p>}
              {labsState === 'restricted' && <p>Laboratory results require patient-record access.</p>}
              {labsState === 'ready' && (
                <dl className="grid gap-4 sm:grid-cols-2">
                  {labGroups
                    .filter((g) => g.label !== 'Additional reviewed tests' || g.results.length)
                    .map((group) =>
                      field(
                        group.label,
                        group.results.length ? (
                          <ul className="flex flex-col gap-2">
                            {group.results.map((result) => (
                              <li key={result.id}>
                                <span>
                                  {result.test_name}: {result.value || 'Value not documented'} {result.unit}
                                </span>
                                <small className="block text-muted-foreground">
                                  {result.collected_date
                                    ? `Collected ${result.collected_date}`
                                    : result.reported_date
                                      ? `Reported ${result.reported_date}`
                                      : 'Date not documented'}
                                  {result.status ? ` · ${result.status}` : ''}
                                  {result.flag ? ` · Flag ${result.flag}` : ''}
                                </small>
                                {result.evidence && (
                                  <details>
                                    <summary>Source evidence</summary>
                                    <p className="whitespace-pre-wrap text-sm">{result.evidence}</p>
                                    {result.document_id && (
                                      <small>
                                        Document {result.document_id} · page {result.page}
                                      </small>
                                    )}
                                  </details>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : null,
                      ),
                    )}
                </dl>
              )}
            </section>
            <details>
              <summary>Medication entries and consent decisions documented in this episode</summary>
              <p className="my-2 text-sm text-muted-foreground">
                These entries do not establish a current medication or supplement list. Review the source entry for
                indication, dose, and date.
              </p>
              {data.medications.length || data.decisions.length ? (
                <ul className="flex flex-col gap-2">
                  {[...data.medications, ...data.decisions].map((event) => (
                    <li key={event.id}>
                      {formatInstant(event.occurred_at, 'MMM d, yyyy HH:mm', timeZone)} ·{' '}
                      {displayPayload(event.payload)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No confirmed entries documented in this episode.</p>
              )}
            </details>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
